import { ChatbotMessage } from "@core/domain/models/chatbot-message.model";
import {
    BaseViewModel,
    ChatbotMessagesDeleteUseCase,
    ChatbotMessagesGetAllUseCase,
    ChatbotSendMessageUseCase,
    ChatbotWeeklyReportUseCase,
} from "@iot-platform/core";
import { inject, injectable } from "inversify";
import {
    action,
    computed,
    makeObservable,
    observable,
    runInAction,
} from "mobx";

@injectable()
export class ChatbotViewModel extends BaseViewModel {
    @observable
    isLoadingMessages = false;

    @observable
    isFirstLoadInProgress = false;

    @observable
    isHistoryLoaded = false;

    @observable
    messages: readonly Readonly<ChatbotMessage>[] = [];

    constructor(
        @inject(ChatbotMessagesDeleteUseCase)
        private readonly messagesDeleteUseCase: ChatbotMessagesDeleteUseCase,
        @inject(ChatbotSendMessageUseCase)
        private readonly chatbotSendMessageUseCase: ChatbotSendMessageUseCase,
        @inject(ChatbotMessagesGetAllUseCase)
        private readonly chatbotMessagesGetAllUseCase: ChatbotMessagesGetAllUseCase,
        @inject(ChatbotWeeklyReportUseCase)
        private readonly chatbotWeeklyReportUseCase: ChatbotWeeklyReportUseCase,
    ) {
        super();
        makeObservable(this);
    }

    @computed
    get isInputDisabled(): boolean {
        return this.isFirstLoadInProgress || this.isLoadingMessages;
    }

    @action
    async onOpen(): Promise<void> {
        // If the history has already been loaded once in this session, do nothing
        if (this.isHistoryLoaded) return;

        // Set "loading first open" flag
        this.isFirstLoadInProgress = true;

        // 1. Try to get existing history from backend
        const existing = await this.chatbotMessagesGetAllUseCase.execute();
        let newMessages = existing.chatbotMessages;

        // 2. If there is no history, request the weekly report
        if (newMessages.length === 0) {
            const weekly = await this.chatbotWeeklyReportUseCase.execute();
            newMessages = weekly.chatbotMessages;
        }

        // 3. Update state
        runInAction(() => {
            this.messages = newMessages; // save messages (history, report or fallback)
            this.isHistoryLoaded = true; // mark that history has been loaded once
            this.isFirstLoadInProgress = false; // disable initial loading flag
        });
    }

    @action
    async getMessages(): Promise<void> {
        const messagesResult =
            await this.chatbotMessagesGetAllUseCase.execute();

        runInAction(() => {
            this.messages = messagesResult.chatbotMessages;
        });
    }

    @action
    async deleteMessages(): Promise<void> {
        const result = await this.messagesDeleteUseCase.execute();

        runInAction(() => {
            if (result) {
                this.messages = [];
                this.isHistoryLoaded = false;
            }
        });
    }

    @action
    async sendMessage(content: string): Promise<void> {
        // Temporary user message
        const tempUserMsg = ChatbotMessage.create({
            role: "user",
            content,
        })._unsafeUnwrap();

        runInAction(() => {
            this.messages = [...this.messages, tempUserMsg];
            this.isLoadingMessages = true;
        });

        const result = await this.chatbotSendMessageUseCase.execute(content);

        runInAction(() => {
            this.messages = result.chatbotMessages;
            this.isLoadingMessages = false;
        });
    }
}
